import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
// react-router-dom removed - using state-based navigation
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Container, Box, CircularProgress, Typography, Button, Paper, Snackbar, Alert } from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import theme from './theme';
import api from './api';
import ErrorBoundary from './components/ErrorBoundary';
import { GLASS_BG, GLASS_BORDER, PRIMARY, PRIMARY_GRADIENT, PRIMARY_GRADIENT_HOVER, TEXT_SECONDARY, TEXT_PRIMARY, TEXT_DISABLED, TEXT_MUTED } from './colors';
import ConsentBanner from './components/ConsentBanner';

// Eagerly loaded components (critical path)
import Header from './components/Header';
import Login from './components/Login';

// Only log in development mode
const isDev = process.env.NODE_ENV === 'development';
const devLog = (...args) => { if (isDev) console.log(...args); };

// Lazy loaded components (code splitting for better performance)
const Register = lazy(() => import('./components/Register'));
const AddVehicle = lazy(() => import('./components/AddVehicle'));
const VehicleList = lazy(() => import('./components/VehicleList'));
const AddMaintenance = lazy(() => import('./components/AddMaintenance'));
const MaintenanceList = lazy(() => import('./components/MaintenanceList'));
const UpgradeModal = lazy(() => import('./components/UpgradeModal'));
const Sidebar = lazy(() => import('./components/Sidebar'));
const ReminderSettings = lazy(() => import('./components/ReminderSettings'));
const CsvImport = lazy(() => import('./components/CsvImport'));
const ComplianceTracker = lazy(() => import('./components/ComplianceTracker'));
const FuelLog = lazy(() => import('./components/FuelLog'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const About = lazy(() => import('./components/About'));
const Contact = lazy(() => import('./components/Contact'));
const Profile = lazy(() => import('./components/Profile'));
const Settings = lazy(() => import('./components/Settings'));
// WelcomeBanner removed - replaced with simpler empty state
const DashboardStats = lazy(() => import('./components/DashboardStats'));
const DashboardAttentionSummary = lazy(() => import('./components/DashboardAttentionSummary'));
const UpcomingAlerts = lazy(() => import('./components/UpcomingAlerts'));
const Footer = lazy(() => import('./components/Footer'));
const PasswordResetRequest = lazy(() => import('./components/PasswordResetRequest'));
const PasswordResetConfirm = lazy(() => import('./components/PasswordResetConfirm'));
const EmailVerification = lazy(() => import('./components/EmailVerification'));
const ResendVerification = lazy(() => import('./components/ResendVerification'));
const SubscriptionSuccess = lazy(() => import('./components/SubscriptionSuccess'));
const Reports = lazy(() => import('./components/Reports'));
const ScheduledMaintenance = lazy(() => import('./components/ScheduledMaintenance'));
const DashboardAnalytics = lazy(() => import('./components/DashboardAnalytics'));
const Expenses = lazy(() => import('./components/Expenses'));
const Documents = lazy(() => import('./components/Documents'));
const TeamManagement = lazy(() => import('./components/TeamManagement'));
const QuickActionFAB = lazy(() => import('./components/QuickActionFAB'));
const ReceiptScanner = lazy(() => import('./components/ReceiptScanner'));
const BottomNav = lazy(() => import('./components/BottomNav'));
const OnboardingTour = lazy(() => import('./components/OnboardingTour'));
const FeatureAnnouncementBanner = lazy(() => import('./components/FeatureAnnouncementBanner'));
const WelcomeModal = lazy(() => import('./components/WelcomeModal'));
const OnboardingChecklist = lazy(() => import('./components/OnboardingChecklist'));
const FeaturePreviewCarousel = lazy(() => import('./components/FeaturePreviewCarousel'));

const VehicleLanding = lazy(() => import('./components/VehicleLanding'));

// Loading fallback component
const LoadingFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
    <CircularProgress sx={{ color: PRIMARY }} />
  </Box>
);

function App() {
  // Check URL for special routes (password reset, email verification, subscription success)
  const urlParams = new URLSearchParams(window.location.search);
  const isPasswordResetUrl = urlParams.has('reset_token') && urlParams.has('reset_uid');
  const isEmailVerificationUrl = urlParams.has('verification_token');
  const isSubscriptionSuccessUrl = urlParams.has('session_id');
  const isPortalReturn = urlParams.has('from_portal');
  
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  
  const [showLanding, setShowLanding] = useState(!token && !isSubscriptionSuccessUrl);
  const [showRegister, setShowRegister] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [showPasswordResetConfirm, setShowPasswordResetConfirm] = useState(isPasswordResetUrl);
  const [showEmailVerification] = useState(isEmailVerificationUrl);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [showSubscriptionSuccess, setShowSubscriptionSuccess] = useState(isSubscriptionSuccessUrl);
  const [showProfile, setShowProfile] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showTermsOfService, setShowTermsOfService] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [maintenanceRefresh, setMaintenanceRefresh] = useState(0);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalMode, setUpgradeModalMode] = useState('default'); // 'default' | 'locked-nav' | 'feature-specific'
  const [upgradeModalFeature, setUpgradeModalFeature] = useState(null); // 'ai-recommendations' | 'reminders' | 'csv-import' | 'compliance'
  const [showFeaturePreview, setShowFeaturePreview] = useState(false); // Show feature preview carousel before upgrade
  const [planSummary, setPlanSummary] = useState('');
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [currentView, setCurrentView] = useState('dashboard');
  const [initialMaintenanceVehicleId, setInitialMaintenanceVehicleId] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const dashboardTab = 0;
  const setDashboardTab = () => {}; // no-op
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isLoadingData, setIsLoadingData] = useState(true); // Track initial data loading

  const handleFetchErrorClose = () => setFetchError('');

  const [userInfo, setUserInfo] = useState(null);
  const [runTour, setRunTour] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const hasLoadedAuthDataRef = useRef(false);

  // Debug rendering conditions
  useEffect(() => {
    devLog('[RENDER] Current state:', {
      token: !!token,
      showLanding,
      showRegister,
      currentView,
    });
  }, [token, showLanding, showRegister, currentView]);

  const canSeeVehicles = true;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogin = async (username, password) => {
    try {
      devLog('[LOGIN] Starting login process...');
      const response = await api.post('/auth/login/', { username, password });
      const { access, refresh, user } = response.data;
      localStorage.setItem('token', access);
      localStorage.setItem('refresh', refresh);
      localStorage.setItem('userInfo', JSON.stringify(user));
      devLog('[LOGIN] Tokens stored:', { access: !!access, refresh: !!refresh });
      devLog('[LOGIN] User info:', { username: user.username, persona: user.persona });
      setUserInfo(user);
      await fetchSubscriptionStatus({ forceFresh: true }); // Force fresh fetch on login
      setToken(access);
      devLog('[LOGIN] Setting token state and hiding landing');
      setShowLanding(false);
      
      // Check if this is first-time registration (flag set by Register component)
      const isFirstRegistration = localStorage.getItem('firstTimeRegistration');
      if (isFirstRegistration === 'true') {
        devLog('[LOGIN] First registration detected, showing welcome modal');
        setShowWelcomeModal(true);
        localStorage.removeItem('firstTimeRegistration');
      }
      
      // Check if there's a pending session to verify (from Stripe redirect after token expiry)
      const pendingSessionId = localStorage.getItem('pendingSessionId');
      if (pendingSessionId) {
        devLog('[LOGIN] Found pending session, verifying:', pendingSessionId);
        try {
          await api.post('/subscription/verify-session/', { session_id: pendingSessionId });
          devLog('[LOGIN] Pending session verified successfully');
          localStorage.removeItem('pendingSessionId');
          // Refresh subscription data after verification
          await fetchSubscriptionStatus({ forceFresh: true }); // Force fresh after Stripe verification
        } catch (verifyErr) {
          console.warn('[LOGIN] Failed to verify pending session:', verifyErr);
          localStorage.removeItem('pendingSessionId');
        }
      }
      
      devLog('[LOGIN] Setting currentView to dashboard');
      setCurrentView('dashboard');
      applyMemberExperienceDefaults();
      
      // Replace history to prevent back button from showing login page
      devLog('[LOGIN] Replacing history with /dashboard');
      window.history.replaceState({ page: 'app', view: 'dashboard' }, '', '/dashboard');
    } catch (error) {
      console.error('Login error:', error.response?.data);
      throw error; // Re-throw to be caught by Login component
    }
  };

  // Handle Google OAuth login
  const handleGoogleLogin = async (authData) => {
    try {
      // authData already contains access, refresh, and user from the Login component's API call
      const { access, refresh, user } = authData;
      localStorage.setItem('token', access);
      localStorage.setItem('refresh', refresh);
      localStorage.setItem('userInfo', JSON.stringify(user));
      devLog('[GOOGLE LOGIN] Tokens stored:', { access: !!access, refresh: !!refresh });
      setUserInfo(user);
      await fetchSubscriptionStatus({ forceFresh: true }); // Force fresh fetch on Google login
      setToken(access);
      setShowLanding(false);

      setCurrentView('dashboard');
      applyMemberExperienceDefaults();
      
      // Replace history to prevent back button from showing login page
      window.history.replaceState({ page: 'app', view: 'dashboard' }, '', '/dashboard');
    } catch (error) {
      console.error('[GOOGLE LOGIN] Error:', error);
      throw error;
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('cachedSubscriptionData'); // Clear subscription cache on logout
    setToken(null);
    setUserInfo(null);
    setVehicles([]);
    setMaintenance([]);
    setSubscriptionData(null);
    setShowLanding(true);
    setShowRegister(false);
    setPlanSummary('');
    setCurrentView('dashboard');
    
    // Replace history to prevent back button from showing protected pages
    window.history.replaceState({ page: 'choice' }, '', '/');
  };

  const goToLanding = () => {
    setShowProfile(false);
    setShowPrivacyPolicy(false);
    setShowTermsOfService(false);
    setShowAbout(false);
    setShowContact(false);
    setShowLanding(true);
    window.history.pushState({ page: 'landing' }, '', '/');
  };

  const goToDashboard = () => {
    setShowProfile(false);
    setShowPrivacyPolicy(false);
    setShowTermsOfService(false);
    setShowAbout(false);
    setShowContact(false);
    setShowLanding(false);
    setCurrentView('dashboard');
    window.history.pushState({ page: 'dashboard' }, '', '/dashboard');
  };

  // Welcome Modal Handlers
  const handleWelcomeGetStarted = (dontShowAgain) => {
    // Close modal and navigate to Add Vehicle
    setShowWelcomeModal(false);
    if (dontShowAgain) {
      localStorage.setItem('skipWelcomeModal', 'true');
    }
    handleNavigate('/add-vehicle');
  };

  const handleWelcomeSkip = (dontShowAgain) => {
    // Close modal but stay on dashboard
    setShowWelcomeModal(false);
    if (dontShowAgain) {
      localStorage.setItem('skipWelcomeModal', 'true');
    }
  };

  const handleWelcomeLearnMore = () => {
    // Close the welcome modal and leave the user on the dashboard checklist.
    setShowWelcomeModal(false);
    setCurrentView('dashboard');
    window.history.replaceState({ page: 'app', view: 'dashboard' }, '', '/dashboard');
  };

  // Helper to open upgrade modal with feature-specific context
  // eslint-disable-next-line no-unused-vars
  const showUpgradeWithFeature = (featureName, showPreview = false) => {
    setUpgradeModalFeature(featureName);
    setUpgradeModalMode('feature-specific');
    if (showPreview) {
      setShowFeaturePreview(true);
    } else {
      setShowUpgradeModal(true);
    }
  };

  // Helper function to get quota limit from subscription plan
  const getAssetQuotaLimit = () => {
    if (!subscriptionData) return 3; // Default to Free plan limit
    const plan = subscriptionData.plan?.toLowerCase();
    if (plan === 'fleet') return 999; // Fleet is effectively unlimited
    if (plan === 'pro') return 15; // Pro plan
    return 3; // Free plan default
  };

  const [fetchError, setFetchError] = useState('');

  const fetchVehicles = async () => {
    try {
      const response = await api.get('/vehicles/');
      const data = response.data;
      const vehiclesList = Array.isArray(data) ? data : (data.results || []);
      setVehicles(vehiclesList);
      setFetchError('');
      return vehiclesList; // Return for onboarding check
    } catch (error) {
      if (error.response?.status === 401) {
        // Token is invalid, clear it and redirect to landing
        localStorage.removeItem('token');
        localStorage.removeItem('refresh');
        setToken(null);
        setShowLanding(true);
        setPlanSummary('');
      } else {
        console.error('Failed to fetch vehicles:', error);
        setFetchError("We couldn't load your vehicles. Check your connection and retry.");
      }
      return []; // Return empty array on error
    }
  };

  const fetchMaintenance = async () => {
    try {
      const response = await api.get('/maintenance/');
      const data = response.data;
      setMaintenance(Array.isArray(data) ? data : (data.results || []));
    } catch (error) {
      console.error('Failed to fetch maintenance:', error);
    }
  };

  const fetchReminders = async () => {
    try {
      const response = await api.get('/reminders/');
      const data = response.data;
      setReminders(Array.isArray(data) ? data : (data.results || []));
    } catch (error) {
      console.error('Failed to fetch reminders:', error);
    }
  };

  const applyMemberExperienceDefaults = () => {
    setDashboardTab(0);
  };

  const fetchSubscriptionStatus = async (options = {}) => {
    const { forceFresh = false } = options;
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('No token available for subscription status');
      return;
    }
    await new Promise(resolve => setTimeout(resolve, 100));  // Small delay to ensure storage
    try {
      const response = await api.get('/subscription/status/');
      const d = response.data;
      
      // Check if subscription changed (e.g., admin upgraded user)
      const cachedSubscription = localStorage.getItem('cachedSubscriptionData');
      if (cachedSubscription && !forceFresh) {
        try {
          const cached = JSON.parse(cachedSubscription);
          // If plan or active status changed, it's a significant change - clear cache and reload
          if (cached.plan !== d.plan || cached.active !== d.active) {
            console.log('Subscription changed! Old:', cached.plan, cached.active, '→ New:', d.plan, d.active);
            localStorage.removeItem('cachedSubscriptionData');
            // Show notification to user
            setSnackbarMessage(`Your subscription has been updated to ${d.plan.toUpperCase()}${d.active ? '' : ' (inactive)'}!`);
            setSnackbarOpen(true);
          }
        } catch (e) {
          console.warn('Failed to compare cached subscription:', e);
        }
      }
      
      // Set subscription data immediately on success
      setSubscriptionData(d);
      // Cache the data for fallback on rate limit errors
      localStorage.setItem('cachedSubscriptionData', JSON.stringify(d));
      
      // Build and set planSummary (same logic as refreshSubscription)
      const vehicleCount = d.vehicle_count || 0;
      const assetCount = d.asset_count || vehicleCount;
      const assetLimit = d.asset_limit || d.vehicle_limit || 1;
      
      let assetBreakdown = '';
      if (vehicleCount > 0) {
        assetBreakdown = `${vehicleCount} vehicle${vehicleCount !== 1 ? 's' : ''}`;
      } else {
        assetBreakdown = '0 vehicles';
      }
      
      const limitText = assetLimit === -1
        ? `Unlimited vehicles (${assetBreakdown})`
        : `${assetCount}/${assetLimit} vehicles (${assetBreakdown})`;
      const planSummaryText = `${d.plan.toUpperCase()} • ${limitText}`;
      setPlanSummary(planSummaryText);
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
      // If rate limited (429) or forbidden (403), try to use cached data from localStorage
      // BUT if forceFresh is true (e.g., on login), don't use cache - let user see error
      if (!forceFresh && (error.response?.status === 429 || error.response?.status === 403)) {
        const cachedSubscription = localStorage.getItem('cachedSubscriptionData');
        if (cachedSubscription) {
          try {
            const parsedData = JSON.parse(cachedSubscription);
            console.log('Using cached subscription data:', parsedData);
            setSubscriptionData(parsedData);
          } catch (parseError) {
            console.error('Failed to parse cached subscription data:', parseError);
          }
        }
      }
      // Don't set subscriptionData to null on error - preserve any existing data
    }
  };

  const refreshSubscription = async (options = {}) => {
    const { forceSync = false } = options;
    try {
      const config = forceSync ? { params: { sync: 'true' } } : {};
      const res = await api.get('/subscription/status/', config);
      const d = res.data;
      
      // Check if subscription changed during refresh
      const cachedSubscription = localStorage.getItem('cachedSubscriptionData');
      if (cachedSubscription) {
        try {
          const cached = JSON.parse(cachedSubscription);
          if (cached.plan !== d.plan || cached.active !== d.active) {
            console.log('Subscription changed during refresh! Old:', cached.plan, cached.active, '→ New:', d.plan, d.active);
            localStorage.removeItem('cachedSubscriptionData');
            setSnackbarMessage(`Your subscription has been updated to ${d.plan.toUpperCase()}${d.active ? '' : ' (inactive)'}!`);
            setSnackbarOpen(true);
          }
        } catch (e) {
          console.warn('Failed to compare cached subscription:', e);
        }
      }
      
      setSubscriptionData(d);
      // Cache the data for fallback on rate limit errors
      localStorage.setItem('cachedSubscriptionData', JSON.stringify(d));
      
      // Build asset breakdown string
      const vehicleCount = d.vehicle_count || 0;
      const assetCount = d.asset_count || vehicleCount;
      const assetLimit = d.asset_limit || d.vehicle_limit || 1;
      
      let assetBreakdown = '';
      if (vehicleCount > 0) {
        assetBreakdown = `${vehicleCount} vehicle${vehicleCount !== 1 ? 's' : ''}`;
      } else {
        assetBreakdown = '0 vehicles';
      }
      // Avoid duplicate declaration: use a different variable name here
      const limitText2 = assetLimit === -1
        ? `Unlimited vehicles (${assetBreakdown})`
        : `${assetCount}/${assetLimit} vehicles (${assetBreakdown})`;
      const planName = (d.plan || '').toString().toUpperCase();
      const planSummaryText = planName ? `${planName} • ${limitText2}` : limitText2;
      devLog('[SUBSCRIPTION DEBUG] Setting planSummary to:', planSummaryText);
      setPlanSummary(planSummaryText);

      if (forceSync) {
        const syncSource = d.sync_source || d.syncSource;
        if (syncSource) {
          console.log(`[SUBSCRIPTION] Force sync completed (${syncSource}).`);
        } else {
          devLog('[SUBSCRIPTION] Force sync completed.');
        }
      }
    } catch (error) {
      if (error.response?.status === 401) {
        // Token is invalid, clear it and redirect to landing
        localStorage.removeItem('token');
        localStorage.removeItem('refresh');
        setToken(null);
        setShowLanding(true);
        setPlanSummary('');
      }
    }
  };

  const handleNavigate = (path) => {
    const viewName = path.slice(1); // remove leading /
    setCurrentView(viewName);
    window.history.pushState({ page: 'app', view: viewName }, '', path);
  };

  const renderCurrentView = () => {
    // Show loading spinner while data is being fetched
    if (isLoadingData) {
      return (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '60vh',
          flexDirection: 'column',
          gap: 2
        }}>
          <CircularProgress sx={{ color: PRIMARY }} size={48} />
          <Typography sx={{ color: TEXT_SECONDARY }}>
            Loading your dashboard...
          </Typography>
        </Box>
      );
    }

    switch (currentView) {
      case 'dashboard': {
        const isProOrFleet = subscriptionData?.plan === 'pro' || subscriptionData?.plan === 'fleet';
        const hasVehicles = vehicles.length > 0;
        const quickActionButtons = [];

        if (hasVehicles) {
          quickActionButtons.push(
            <Button
              key="vehicle-upcoming"
              size="small"
              variant="outlined"
              onClick={() => setCurrentView('scheduled-maintenance')}
              sx={{ borderColor: PRIMARY_GRADIENT, color: PRIMARY }}
            >
              📅 Upcoming Services
            </Button>
          );
          quickActionButtons.push(
            <Button
              key="vehicle-compliance"
              size="small"
              variant="outlined"
              onClick={() => setCurrentView('compliance')}
              sx={{ borderColor: TEXT_DISABLED, color: TEXT_PRIMARY }}
            >
              ✓ Vehicle Compliance
            </Button>
          );
        }

        if (hasVehicles && subscriptionData?.notifications_enabled) {
          quickActionButtons.push(
            <Button
              key="reminders"
              size="small"
              variant="outlined"
              onClick={() => setCurrentView('reminders')}
              sx={{ borderColor: 'rgba(255, 255, 255, 0.3)', color: TEXT_SECONDARY }}
            >
              🔔 Reminders
            </Button>
          );
        }

        return (
          <>
            {/* Simple empty state - ONE clear action */}
            {vehicles.length === 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  mb: 3,
                  background: GLASS_BG,
                  border: `1px solid ${GLASS_BORDER}`,
                  borderRadius: 3,
                  textAlign: 'center',
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#fff', mb: 1 }}>
                  Welcome to FleetPulse!
                </Typography>
                <Typography variant="body1" sx={{ color: TEXT_SECONDARY, mb: 4, maxWidth: 400, mx: 'auto' }}>
                  Add your first vehicle to see its maintenance picture at a glance and stay ahead of upcoming work.
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                    gap: 1.5,
                    maxWidth: 680,
                    mx: 'auto',
                    mb: 4,
                  }}
                >
                  {[
                    ['1', 'Add a vehicle', 'Year, make, model, and mileage'],
                    ['2', 'Log a service', 'Capture the latest work'],
                    ['3', 'Set a reminder', 'Stay ahead of the next due date'],
                  ].map(([step, title, description]) => (
                    <Box
                      key={step}
                      sx={{
                        p: 1.5,
                        textAlign: 'left',
                        borderRadius: 2,
                        background: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: PRIMARY, fontWeight: 700 }}>
                        STEP {step}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#fff', fontWeight: 700, mt: 0.5 }}>
                        {title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: TEXT_SECONDARY }}>
                        {description}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Button
                  variant="contained"
                  size="large"
                  onClick={() => handleNavigate('/add-vehicle')}
                  startIcon={<DirectionsCarIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    px: 4,
                    py: 1.5,
                    background: PRIMARY_GRADIENT,
                    '&:hover': { background: PRIMARY_GRADIENT_HOVER },
                    boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
                  }}
                >
                  Add Your First Vehicle
                </Button>
                <Typography variant="caption" sx={{ display: 'block', color: TEXT_MUTED, mt: 1.5 }}>
                  You can edit the details or add more vehicles anytime.
                </Typography>
              </Paper>
            )}

            {/* Onboarding Checklist - starts after the first vehicle is added */}
            {vehicles.length > 0 && (
              <Suspense fallback={null}>
                <OnboardingChecklist
                  vehicleCount={vehicles.length}
                  maintenanceCount={maintenance.length}
                  hasReminders={reminders.length > 0}
                  isPro={subscriptionData?.plan === 'pro' || subscriptionData?.plan === 'fleet'}
                  onNavigate={handleNavigate}
                  onUpgrade={() => setShowUpgradeModal(true)}
                  onDismiss={() => localStorage.setItem('hasSeenOnboardingChecklist', 'true')}
                />
              </Suspense>
            )}

            {vehicles.length > 0 && (
              <>
                <DashboardStats
                  vehicles={vehicles}
                  maintenance={maintenance}
                  subscriptionPlan={subscriptionData?.plan}
                  onUpgrade={() => setShowUpgradeModal(true)}
                  onAddVehicle={() => setCurrentView('vehicles')}
                  canSeeVehicles={canSeeVehicles}
                />

                <Suspense fallback={null}>
                  <DashboardAttentionSummary
                    vehicles={vehicles}
                    maintenance={maintenance}
                    onNavigateToMaintenance={() => setCurrentView('maintenance')}
                    onNavigateToCompliance={() => setCurrentView('compliance')}
                  />
                </Suspense>

                {/* Upcoming Alerts - Available to ALL users (basic in-app reminders) */}
                <Suspense fallback={null}>
                  <UpcomingAlerts
                    onUpgrade={() => setShowUpgradeModal(true)}
                    canSeeVehicles={canSeeVehicles}
                  />
                </Suspense>
              </>
            )}

            {/* Quick Actions for Pro/Fleet Users */}
            {vehicles.length > 0 && isProOrFleet && quickActionButtons.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {quickActionButtons}
                </Box>
              </Box>
            )}
            
            {/* Fleet controls appear once the first vehicle exists. */}
            {canSeeVehicles && vehicles.length > 0 && (
              <>
                <VehicleList 
                  token={token} 
                  vehicles={vehicles} 
                  maintenance={maintenance}
                  planSummary={planSummary} 
                  quotaLimit={getAssetQuotaLimit()}
                  onDelete={() => {
                    fetchVehicles();
                    fetchMaintenance();
                  }} 
                  onMaintenanceAdd={() => {
                    fetchMaintenance();
                    setMaintenanceRefresh(prev => prev + 1);
                  }}
                  onLogout={handleLogout} 
                  onUpgrade={() => setShowUpgradeModal(true)}
                  onAddVehicle={() => setCurrentView('add-vehicle')}
                />
                <MaintenanceList 
                  token={token} 
                  vehicles={vehicles} 
                  refreshTrigger={maintenanceRefresh}
                  subscriptionPlan={subscriptionData?.plan}
                  onUpgrade={() => setShowUpgradeModal(true)}
                  onAddMaintenance={() => setCurrentView('add-maintenance')}
                  onNavigateToReminders={() => setCurrentView('reminders')}
                  onNavigateToScheduled={() => setCurrentView('scheduled-maintenance')}
                  onNavigateToCompliance={() => setCurrentView('compliance')}
                />
              </>
            )}
          </>
        );
      }
      case 'vehicles':
        return (
          <>
            <VehicleList 
              token={token} 
              vehicles={vehicles} 
              maintenance={maintenance}
              planSummary={planSummary} 
              quotaLimit={getAssetQuotaLimit()}
              onDelete={fetchVehicles} 
              onMaintenanceAdd={() => setMaintenanceRefresh(prev => prev + 1)}
              onLogout={handleLogout} 
              onUpgrade={() => setShowUpgradeModal(true)}
              onAddVehicle={() => setCurrentView('add-vehicle')}
              onNavigateToMaintenance={() => setCurrentView('maintenance')}
              onNavigateToAddMaintenance={() => setCurrentView('add-maintenance')}
            />
          </>
        );
      case 'add-vehicle':
        return (
          <AddVehicle 
            onBack={() => setCurrentView('vehicles')}
            onVehicleAdded={() => {
              fetchVehicles();
              fetchMaintenance();
              setCurrentView('vehicles');
            }}
            onLimitReached={() => setShowUpgradeModal(true)}
            onNavigateToMaintenance={(vehicle) => {
              fetchVehicles();
              fetchMaintenance();
              setInitialMaintenanceVehicleId(vehicle?.id ? String(vehicle.id) : '');
              setCurrentView('add-maintenance');
            }}
            onNavigateToReceipt={() => {
              fetchVehicles();
              fetchMaintenance();
              setCurrentView('receipt-scanner');
            }}
          />
        );
      case 'maintenance':
        return (
          <MaintenanceList 
            token={token} 
            vehicles={vehicles} 
            refreshTrigger={maintenanceRefresh}
            subscriptionPlan={subscriptionData?.plan}
            onUpgrade={() => setShowUpgradeModal(true)}
            onAddMaintenance={() => setCurrentView('add-maintenance')}
            onNavigateToReceiptScanner={() => setCurrentView('receipt-scanner')}
            onAddVehicle={() => setCurrentView('add-vehicle')}
            onNavigateToReminders={() => setCurrentView('reminders')}
            onNavigateToScheduled={() => setCurrentView('scheduled-maintenance')}
            onNavigateToCompliance={() => setCurrentView('compliance')}
          />
        );
      case 'add-maintenance':
        return (
          <AddMaintenance 
            vehicles={vehicles} 
            initialVehicleId={initialMaintenanceVehicleId}
            onBack={() => setCurrentView('maintenance')}
            onMaintenanceAdded={() => {
              fetchMaintenance();
              setMaintenanceRefresh(prev => prev + 1);
              setCurrentView('maintenance');
            }}
            subscriptionData={subscriptionData}
            onUpgrade={() => setShowUpgradeModal(true)}
            onAddVehicle={() => setCurrentView('add-vehicle')}
          />
        );
      case 'reminders':
        return <ReminderSettings vehicles={vehicles} token={token} onUpgrade={() => setShowUpgradeModal(true)} subscriptionData={subscriptionData} onAddVehicle={() => setCurrentView('add-vehicle')} />;
      case 'import':
        return (
          <Box sx={{ mt: 4 }}>
            <CsvImport 
              token={token}
              onRefresh={() => {
                fetchVehicles();
                setCurrentView('vehicles');
              }}
              onNavigate={(path) => {
                const viewName = path.replace('/', '');
                setCurrentView(viewName);
                window.history.pushState({ page: 'app', view: viewName }, '', path);
              }}
            />
          </Box>
        );
      case 'compliance':
        return (
          <ComplianceTracker 
            vehicles={vehicles}
            subscriptionPlan={subscriptionData?.plan}
            onUpgrade={() => setShowUpgradeModal(true)}
            onAddVehicle={() => setCurrentView('add-vehicle')}
          />
        );
      case 'fuel':
        return (
          <FuelLog 
            vehicles={vehicles}
            subscriptionData={subscriptionData}
            onUpgrade={() => setShowUpgradeModal(true)}
            onBack={() => setCurrentView('dashboard')}
            onAddVehicle={() => setCurrentView('add-vehicle')}
          />
        );
      case 'reports':
        return (
          <Reports 
            token={token}
            subscriptionData={subscriptionData}
            onShowUpgradeModal={() => setShowUpgradeModal(true)}
            vehicles={vehicles}
            onAddVehicle={() => setCurrentView('add-vehicle')}
          />
        );
      case 'scheduled-maintenance':
        return (
          <ScheduledMaintenance
            vehicles={vehicles}
            token={token}
            subscriptionData={subscriptionData}
            onUpgrade={() => setShowUpgradeModal(true)}
            onAddVehicle={() => setCurrentView('add-vehicle')}
            onMaintenanceCompleted={() => {
              fetchMaintenance();
              setMaintenanceRefresh((current) => current + 1);
            }}
          />
        );
      case 'analytics':
        return (
          <DashboardAnalytics
            vehicles={vehicles}
            token={token}
            subscriptionData={subscriptionData}
            onUpgrade={() => setShowUpgradeModal(true)}
            canSeeVehicles={canSeeVehicles}
          />
        );
      case 'expenses':
        return (
          <Expenses vehicles={vehicles} />
        );
      case 'documents':
        return <Documents vehicles={vehicles} />;
      case 'team':
        return (
          <TeamManagement
            subscriptionData={subscriptionData}
            onUpgrade={() => setShowUpgradeModal(true)}
          />
        );
      case 'receipt-scanner':
        return (
          <ReceiptScanner
            vehicles={vehicles}
            token={token}
            subscriptionData={subscriptionData}
            onUpgrade={() => setShowUpgradeModal(true)}
            onMaintenanceAdded={() => {
              fetchMaintenance();
              setCurrentView('maintenance');
            }}
            onFuelAdded={() => {
              setCurrentView('fuel');
            }}
          />
        );
      case 'account':
      case 'settings':
        return (
          <Settings
            onBack={goToDashboard}
            onLanding={goToLanding}
            userInfo={userInfo}
            onUpgrade={() => setShowUpgradeModal(true)}
            subscriptionData={subscriptionData}
            vehicles={vehicles}
            onAddVehicle={() => setCurrentView('add-vehicle')}
          />
        );
      default:
        return <div>Welcome to FleetPulse Dashboard</div>;
    }
  };

  // Auth guard: Check token validity on mount and validate
  useEffect(() => {
    const validateToken = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Validate token by making a request to a non-rate-limited endpoint
          await api.get('/vehicles/'); // Use vehicles endpoint instead of rate-limited subscription/status
          devLog('[Auth Guard] Token is valid');
        } catch (error) {
          if (error.response?.status === 401) {
            console.warn('[Auth Guard] Token invalid, logging out');
            handleLogout();
          }
        }
      }
    };
    validateToken();
  }, []);

  // Manage marketing screens when auth state changes
  useEffect(() => {
    if (isSubscriptionSuccessUrl) {
      setShowLanding(false);
      return;
    }

    if (token) {
      setShowLanding(false);
      return;
    }

    setShowLanding(true);
  }, [token, isSubscriptionSuccessUrl]);

  useEffect(() => {
    let isCancelled = false;

    const loadData = async () => {
      const authToken = token || localStorage.getItem('token');
      if (!authToken) {
        if (!isCancelled) {
          setIsLoadingData(false);
        }
        return;
      }

      if (!isCancelled) {
        setIsLoadingData(true);
      }

      const storedUserInfo = localStorage.getItem('userInfo');
      if (storedUserInfo && !isCancelled) {
        setUserInfo(JSON.parse(storedUserInfo));
      }

      const shouldForceSync = isPortalReturn || isSubscriptionSuccessUrl;
      const localSyncFlag = localStorage.getItem('forceSubscriptionSync') === 'true';
      const needsFreshSubscription = !hasLoadedAuthDataRef.current || shouldForceSync || localSyncFlag;
      try {
        await fetchVehicles();

        await Promise.all([
          fetchMaintenance(),
          fetchReminders(),
          fetchSubscriptionStatus({ forceFresh: needsFreshSubscription }),
        ]);

        if (shouldForceSync || localSyncFlag) {
          await refreshSubscription({ forceSync: true });
        }

        if (localSyncFlag) {
          localStorage.removeItem('forceSubscriptionSync');
        }

        hasLoadedAuthDataRef.current = true;
      } finally {
        if (!isCancelled) {
          setIsLoadingData(false);
          // The dashboard checklist is the single first-run guide. The old
          // sidebar tour was too abstract before a user had any fleet data.
        }
      }

      if (isCancelled) {
        return;
      }

      if (isPortalReturn) {
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.delete('from_portal');
        const newQuery = currentParams.toString();
        const newUrl = `${window.location.pathname}${newQuery ? `?${newQuery}` : ''}`;
        window.history.replaceState(window.history.state || {}, '', newUrl || window.location.pathname);
      }

      const hasSeenWelcome = localStorage.getItem('hasSeenWelcome');
      if (!hasSeenWelcome && !isCancelled) {
        setShowWelcome(true);
      }
    };

    loadData();

    return () => {
      isCancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isPortalReturn, isSubscriptionSuccessUrl]);

  useEffect(() => {
    // Hide welcome banner once user adds first vehicle
    if (vehicles.length > 0 && showWelcome) {
      setShowWelcome(false);
      localStorage.setItem('hasSeenWelcome', 'true');
    }
  }, [vehicles, showWelcome]);

  // Prevent the onboarding tour from blocking non-dashboard flows (e.g., analytics)
  useEffect(() => {
    if (runTour && currentView !== 'dashboard') {
      localStorage.setItem('onboarding_tour_completed', 'true');
      setRunTour(false);
    }
  }, [runTour, currentView]);

  // Restore view from URL on mount (handles page refresh)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const pathname = window.location.pathname;
    const viewFromUrl = pathname.slice(1); // Remove leading /
    
    // Valid app views that should be restored on refresh
    const validViews = [
      'dashboard', 'vehicles', 'add-vehicle', 'maintenance', 'add-maintenance',
      'compliance',
      'reminders', 'csv-import', 'import', 'fuel',
      'receipt-scanner', 'scheduled-maintenance', 'analytics', 'reports',
      'expenses', 'documents', 'team', 'account', 'settings'
    ];
    
    if (token) {
      if (viewFromUrl && validViews.includes(viewFromUrl)) {
        // Restore the view from URL
        setCurrentView(viewFromUrl);
        window.history.replaceState({ page: 'app', view: viewFromUrl }, '', pathname);
      } else if (!window.history.state) {
        // No valid view in URL, default to dashboard
        setCurrentView('dashboard');
        window.history.replaceState({ page: 'app', view: 'dashboard' }, '', '/dashboard');
      }
    } else {
      // No token - default to landing page
      if (viewFromUrl && validViews.includes(viewFromUrl)) {
        window.history.replaceState({ page: 'landing' }, '', '/');
      } else if (!window.history.state) {
        window.history.replaceState({ page: 'landing' }, '', '/');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event) => {
      const state = event.state;
      
      // Reset all show states first
      setShowLanding(false);
      setShowRegister(false);
      setShowProfile(false);
      setShowPrivacyPolicy(false);
      setShowTermsOfService(false);
      setShowAbout(false);
      setShowContact(false);
      
      // Helper to refresh data when navigating to authenticated views
      const refreshDashboardData = async () => {
        if (token) {
          setIsLoadingData(true);
          try {
            await Promise.all([
              fetchVehicles(),
              fetchMaintenance(),
              fetchReminders(),
            ]);
            await refreshSubscription();
          } finally {
            setIsLoadingData(false);
          }
        }
      };
      
      if (state?.page) {
        // Navigate based on state
        switch (state.page) {
          case 'landing':
            setShowLanding(true);
            break;
          case 'dashboard':
            setCurrentView('dashboard');
            refreshDashboardData();
            break;
          case 'app':
            // Handle authenticated app views (reminders, vehicles, etc.)
            if (state.view) {
              setCurrentView(state.view);
            } else {
              setCurrentView('dashboard');
            }
            refreshDashboardData();
            break;
          case 'profile':
            setShowProfile(true);
            break;
          case 'privacy':
            setShowPrivacyPolicy(true);
            break;
          case 'terms':
            setShowTermsOfService(true);
            break;
          case 'about':
            setShowAbout(true);
            break;
          case 'contact':
            setShowContact(true);
            break;
          default:
            if (token) {
              setCurrentView('dashboard');
              refreshDashboardData();
            } else {
              setShowLanding(true);
            }
        }
      } else {
        // No state - fallback based on current auth status and URL
        if (token) {
          setCurrentView('dashboard');
          refreshDashboardData();
        } else {
          setShowLanding(true);
        }
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [token]);

  // Prevent browser caching of authenticated pages
  useEffect(() => {
    if (token) {
      // Add cache control headers via meta tags
      const metaCache = document.createElement('meta');
      metaCache.httpEquiv = 'Cache-Control';
      metaCache.content = 'no-store, no-cache, must-revalidate, max-age=0';
      document.head.appendChild(metaCache);
      
      return () => {
        document.head.removeChild(metaCache);
      };
    }
  }, [token]);

  // NOTE: Subscription success is now handled by SubscriptionSuccess component
  // which renders when showSubscriptionSuccess && hasSessionId is true.
  // Do NOT clear session_id from URL here - let the component handle verification first.

  if (showPasswordReset) {
    return (
      <ThemeProvider theme={theme}>
        <PasswordResetRequest onBack={() => {
          setShowPasswordReset(false);
          setShowLanding(false);
          setShowRegister(false);
          window.history.replaceState({ page: 'landing', auth: 'login' }, '', '/');
        }} />
      </ThemeProvider>
    );
  }

  if (showPasswordResetConfirm) {
    return (
      <ThemeProvider theme={theme}>
        <PasswordResetConfirm onSuccess={() => {
          setShowPasswordResetConfirm(false);
          setShowLanding(true);
          window.history.replaceState({ page: 'landing' }, '', '/');
        }} />
      </ThemeProvider>
    );
  }

  if (showEmailVerification) {
    return (
      <ErrorBoundary>
        <ThemeProvider theme={theme}>
          <EmailVerification />
        </ThemeProvider>
      </ErrorBoundary>
    );
  }

  if (showResendVerification) {
    return (
      <ErrorBoundary>
        <ThemeProvider theme={theme}>
          <ResendVerification onBack={() => {
            setShowResendVerification(false);
            setShowLanding(true);
          }} />
        </ThemeProvider>
      </ErrorBoundary>
    );
  }

  // Only show subscription success if BOTH state is true AND URL has session_id
  const currentUrlParams = new URLSearchParams(window.location.search);
  const hasSessionId = currentUrlParams.has('session_id');
  
  if (showSubscriptionSuccess && hasSessionId) {
    return (
      <ErrorBoundary>
        <ThemeProvider theme={theme}>
          <SubscriptionSuccess 
            onComplete={() => {
              setShowSubscriptionSuccess(false);
              setCurrentView('dashboard');
              refreshSubscription({ forceSync: true }); // Refresh subscription data with Stripe sync
              window.history.replaceState({ page: 'app', view: 'dashboard' }, '', '/dashboard');
            }}
            onLogin={() => {
              // Store the session_id and redirect to login
              setShowSubscriptionSuccess(false);
              setShowLanding(true);
              window.history.replaceState({ page: 'landing' }, '', '/');
            }}
          />
        </ThemeProvider>
      </ErrorBoundary>
    );
  }
  
  // If state is true but no session_id in URL, clear the state
  if (showSubscriptionSuccess && !hasSessionId) {
    setShowSubscriptionSuccess(false);
  }

  if (showPrivacyPolicy) {
    return (
      <ThemeProvider theme={theme}>
        <PrivacyPolicy 
          onBack={goToLanding} 
          onDashboard={token ? goToDashboard : null}
        />
      </ThemeProvider>
    );
  }

  if (showTermsOfService) {
    return (
      <ThemeProvider theme={theme}>
        <TermsOfService 
          onBack={goToLanding} 
          onDashboard={token ? goToDashboard : null}
        />
      </ThemeProvider>
    );
  }

  if (showAbout) {
    return (
      <ThemeProvider theme={theme}>
        <About 
          onGetStarted={() => {
            setShowAbout(false);
            setShowLanding(false);
            setShowRegister(true);
          }}
          onBack={goToLanding}
          onDashboard={token ? goToDashboard : null}
        />
      </ThemeProvider>
    );
  }

  if (showContact) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          <Header onLogoClick={goToLanding} />
          <Contact />
          <Footer simple onAbout={() => setShowAbout(true)} />
        </Box>
      </ThemeProvider>
    );
  }

  if (showProfile) {
    return (
      <ThemeProvider theme={theme}>
        <Profile 
          onBack={goToDashboard}
          onLanding={goToLanding}
          userInfo={userInfo}
          onUpgrade={() => setShowUpgradeModal(true)}
        />
        <UpgradeModal
          open={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          currentPlan={subscriptionData?.plan || 'free'}
        />
      </ThemeProvider>
    );
  }

  // Common landing page props
  const landingProps = {
    onGetStarted: () => {
      setShowLanding(false);
      setShowRegister(true);
      window.history.pushState({ page: 'landing', auth: 'register' }, '', '/register');
    },
    onSignIn: () => {
      setShowLanding(false);
      setShowRegister(false);
      window.history.replaceState({ page: 'landing', auth: 'login' }, '', '/');
    },
    onPrivacyPolicy: () => {
      setShowPrivacyPolicy(true);
      window.history.pushState({ page: 'privacy' }, '', '/privacy');
    },
    onTermsOfService: () => {
      setShowTermsOfService(true);
      window.history.pushState({ page: 'terms' }, '', '/terms');
    },
    onAbout: () => {
      setShowAbout(true);
      window.history.pushState({ page: 'about' }, '', '/about');
    },
    onContact: () => {
      setShowContact(true);
      window.history.pushState({ page: 'contact' }, '', '/contact');
    },
  };

  if (showLanding) {
    return (
      <ThemeProvider theme={theme}>
        <Suspense fallback={<LoadingFallback />}>
          <VehicleLanding {...landingProps} />
        </Suspense>
        <ConsentBanner />
      </ThemeProvider>
    );
  }

  // Redirect authenticated users away from login page
  if (!token) {
    return (
      <ThemeProvider theme={theme}>
        <Header onLogoClick={goToLanding} />
        <Container maxWidth="xl" sx={{ mt: 4 }}>
          {showRegister ? (
            <Register
              onRegister={() => {
                setShowRegister(false);
                window.history.replaceState({ page: 'landing' }, '', '/');
              }}
              onBackToLogin={() => {
                setShowRegister(false);
                window.history.replaceState({ page: 'landing' }, '', '/');
              }}
              onGoogleLogin={handleGoogleLogin}
              onAutoLogin={handleLogin}
            />
          ) : (
            <Login
              onLogin={handleLogin}
              onGoogleLogin={handleGoogleLogin}
              onSwitchToRegister={() => {
                setShowRegister(true);
                window.history.pushState({}, '', '/register');
              }}
              onForgotPassword={() => {
                setShowLanding(false);
                setShowPasswordReset(true);
              }}
              onResendVerification={() => {
                setShowLanding(false);
                setShowResendVerification(true);
              }}
            />
          )}
        </Container>
        <ConsentBanner />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: 'absolute',
          left: 16,
          top: -100,
          zIndex: (t) => t.zIndex.tooltip,
          px: 2,
          py: 1,
          borderRadius: 1,
          bgcolor: 'primary.main',
          color: '#fff',
          fontWeight: 600,
          textDecoration: 'none',
          '&:focus': { top: 12 },
        }}
      >
        Skip to main content
      </Box>
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <Header
          onLogout={handleLogout}
          showUserMenu={true}
          onProfile={() => {
            setShowProfile(true);
            window.history.pushState({ page: 'profile' }, '', '/profile');
          }}
          onLogoClick={() => {
            setCurrentView('dashboard');
            window.history.pushState({ page: 'app', view: 'dashboard' }, '', '/dashboard');
          }}
          onLanding={goToLanding}
          planSummary={planSummary}
          currentView={currentView}
          onNavigate={handleNavigate}
          userInfo={userInfo}
          subscriptionData={subscriptionData}
          onMenuClick={handleDrawerToggle}
          onUpgrade={() => setShowUpgradeModal(true)}
          canSeeVehicles={canSeeVehicles}
          upgradeModalOpen={showUpgradeModal}
        />
        <Sidebar 
          onNavigate={handleNavigate} 
          mobileOpen={mobileOpen}
          onMobileClose={handleDrawerToggle}
          currentView={currentView}
          onUpgrade={() => {
            setUpgradeModalMode('locked-nav');
            setShowUpgradeModal(true);
          }}
          subscriptionData={subscriptionData}
          upgradeModalOpen={showUpgradeModal}
        />
        <Box
          component="main"
          id="main-content"
          tabIndex={-1}
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 2, md: 3 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mt: '64px',
            minHeight: 'calc(100vh - 64px)',
            overflow: 'hidden',
            // Add bottom padding on mobile for BottomNav
            pb: { xs: '80px', md: 0 },
          }}
        >
          <Box sx={{ 
            flex: 1, 
            width: '100%',
            maxWidth: { xs: '100%', sm: '100%', md: '1200px' },
            overflow: 'hidden',
            pb: 4,
          }}>
            {/* Feature Announcement Banner for logged-in users */}
            {token && currentView === 'dashboard' && (
              <Suspense fallback={null}>
                <FeatureAnnouncementBanner />
              </Suspense>
            )}
            {renderCurrentView()}
          </Box>
          <Footer simple onAbout={() => {
            setShowAbout(true);
            window.history.pushState({ page: 'about' }, '', '/about');
          }} />
        </Box>
        <Snackbar
          open={Boolean(fetchError)}
          onClose={handleFetchErrorClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleFetchErrorClose}
            severity="error"
            sx={{ width: '100%' }}
            variant="filled"
            action={
              <Button color="inherit" size="small" onClick={() => { setFetchError(''); fetchVehicles(); fetchMaintenance(); }}>
                Retry
              </Button>
            }
          >
            {fetchError}
          </Alert>
        </Snackbar>
        {/* Quick Action FAB - Only show when user has at least one vehicle */}
        {token && currentView === 'dashboard' && vehicles.length > 0 && (
          <Suspense fallback={null}>
            <QuickActionFAB
              vehicles={vehicles}
              onAddVehicle={() => handleNavigate('/vehicles')}
              onUploadReceipt={() => handleNavigate('/receipt-scanner')}
              onSuccess={async () => {
                await fetchVehicles();
                await fetchMaintenance();
              }}
              canSeeVehicles={canSeeVehicles}
            />
          </Suspense>
        )}
        {/* Mobile Bottom Navigation */}
        <BottomNav 
          currentView={currentView}
          onNavigate={handleNavigate}
          onMoreClick={handleDrawerToggle}
          canSeeVehicles={canSeeVehicles}
        />
      </Box>
      <ConsentBanner />
      <UpgradeModal
        open={showUpgradeModal}
        mode={upgradeModalMode}
        feature={upgradeModalFeature}
        onClose={() => {
          setShowUpgradeModal(false);
          setUpgradeModalMode('default');
          setUpgradeModalFeature(null);
        }}
        currentPlan={subscriptionData?.plan || 'free'}
      />
      {/* Feature Preview Carousel - shows before upgrade modal */}
      {token && showFeaturePreview && upgradeModalFeature && (
        <Suspense fallback={null}>
          <FeaturePreviewCarousel
            open={showFeaturePreview}
            feature={upgradeModalFeature}
            onClose={() => {
              setShowFeaturePreview(false);
              setUpgradeModalFeature(null);
            }}
            onUpgrade={() => {
              setShowFeaturePreview(false);
              setShowUpgradeModal(true);
            }}
          />
        </Suspense>
      )}
      {/* Welcome Modal for new registrations */}
      {token && showWelcomeModal && (
        <Suspense fallback={null}>
          <WelcomeModal
            open={showWelcomeModal}
            onGetStarted={handleWelcomeGetStarted}
            onSkip={handleWelcomeSkip}
            onLearnMore={handleWelcomeLearnMore}
          />
        </Suspense>
      )}
      {/* Onboarding Tour for new users */}
      {token && (
        <Suspense fallback={null}>
          <OnboardingTour
            run={runTour}
            onComplete={() => setRunTour(false)}
            hasVehicles={vehicles.length > 0}
          />
        </Suspense>
      )}
      {/* Snackbar for subscription change notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}

const AppWithErrorBoundary = () => (
  <ErrorBoundary>
    <Suspense fallback={<LoadingFallback />}>
      <App />
    </Suspense>
  </ErrorBoundary>
);

export default AppWithErrorBoundary;
